<template>
	<div :data-ref="locationType">
		<Switcher :properties="currentWrapper" @close="closeMobileSearch" @enter="focusMobileSearch">
			<!-- Modal trigger -->
			<template #trigger>
				<div v-if="isMobile" :class="{'has-error' : hasError}">
					<!-- Empty input -->
					<div
						v-if="isLocationEmpty(locationType)"
						class="location-selector__placeholder input type--s"
						type="text"
						@click.prevent="openMobileSearch"
					>{{ placeholder }}</div>
					<!-- Selected value -->
					<div v-else class="location-selector__m-selected">
						<p class="location-selector__selected-name type--b">{{ value.primary }}</p>
						<p class="location-selector__selected-address type--s">{{ value.secondary }}</p>
						<p v-if="value.is_private" class="location-selector__approval type--xs">(Subject to operator approval)</p>
						<button class="button vs__clear" @click="clearSelection"></button>
					</div>
				</div>
			</template>

			<!-- Main section -->
			<div :class="['location-selector type--sans', { 'is-mobile' : isMobile }, { 'has-error' : hasError }]">
				<!-- Main vue-select component -->
				<!-- :clearable="!(locationType === 'destination' && this.defaultDestinationLocationSet())" -->
				<v-select
					@search="onInputChange"
					@input="onSelect"
					@search:focus="focusListener"
					:options="isMobile ?  (optionsList.length > 0 ? [...optionsList.slice(0,(optionsList.length > 3 ? 3 : optionsList.length - 1)), ...optionsList.slice(-1)[0]] : []) : optionsList"
					:filterable="false"
					:class="[
						'form-group',
						{ 'is-selected' : !isLocationEmpty(locationType) }
					]"
					:clearable="true"
					:ref="locationType"
					:placeholder="placeholder"
					:closeOnSelect="true"
					:clearSearchOnBlur="() => false"
					:value="value"
					:inputId="inputID"
					label="id"
				>
					<!-- Remove caret icon -->
					<div slot="open-indicator"></div>

					<!-- Custom input field -->
					<template #search="{ attributes, events }">
						<input spellcheck="false" class="vs__search" v-bind="attributes" v-on="events" />
					</template>

					<!-- Loading... -->
					<template #spinner="{ loading }">
						<div v-if="loading" class="location-selector__loading"></div>
					</template>

					<!-- No results found -->
					<template #no-options>
						<div class="type--s">
							<div v-if="isNoResultsVisible" class="location-selector__no-options">No Results</div>
						</div>
					</template>

					<!-- List option -->
					<template #option="option" :ref="locations">

						<div v-if="option.id === optionsList[0].id" class="recommended-head">Fly From A Recommended Landing SIte</div>
						<div v-if="option.isPrivate" class="private-head">Fly from a private landing site</div>

						<!-- Select option component -->
						<SelectOption
							:primary="option.primary"
							:secondary="option.secondary"
							:dataId="option.id"
							:isHovered="option.id === hoveredLocationID"
							:removeTopPadding="(option.id === optionsList[0].id || option.isPrivate)"
							@mouseover="setHoveredLocationID(option.id)"
							@mouseout="setHoveredLocationID(null)"
						>
							<!-- Option icon -->
							<template #icon>
								<component :is="getOptionIcon(option)" :style="{ fill: option.isPrivate ? '#ee9346' : '#5bc1b8' }" />
							</template>
							<!-- Distance in miles, if option type is location -->
							<template #iconCaption>
								<span
									v-if="!option.isGeoArea && !option.isPrivate"
									class="type--xs"
								>{{option.distance}}&nbsp;mi</span>
							</template>
						</SelectOption>
					</template>

					<!-- Selected item display -->
					<template #selected-option-container="{ option }">
						<div class="location-selector__selected">
							<p class="location-selector__selected-name type--b">{{ option.primary }}</p>
							<p class="location-selector__selected-address type--s">{{ option.secondary }}</p>
							<p v-if="option.isPrivate" class="location-selector__approval type--xs">(Subject to operator approval)</p>
						</div>
					</template>
				</v-select>

				<PopularSearches @select="onSelectPopular" :class="[{ 'hidden' : optionsList.length > 0 }]" />
			</div>
		</Switcher>
	</div>
</template>

<script>
/* eslint-disable vue/no-unused-components */

import vSelect from "vue-select"
import PopularSearches from "@/components/formfields/PopularSearches"
import SelectOption from "@/components/formfields/SelectOption"
import Switcher from "@/components/FlytSwitcher"
import FlytModal from "@/components/FlytModal"
import Simple from "@/components/FlytSimple"
import AirportIconWithBorder from "@/assets/flyt-icons_airport--border.svg?inline"
import HelipadIconWithBorder from "@/assets/flyt-icons_heliport--border.svg?inline"
import PinIconWithBorder from "@/assets/flyt-icons_pin.svg?inline"
import ApiService from "@/services/apiService"
import debounce from "lodash.debounce"
import { mapState, mapActions, mapGetters, mapMutations } from "vuex"

// The axios cancel variable has to
// exist outside the component object
let cancel

export default {
	data() {
		return {
			inputID: 'journey_from',
			searchGeoArea: null,
			searchLocations: null,
			optionsList: [],
			numberOfLocationsFound: 0,
			currentPrediction: "",
			searchedAddress2: {},
			isNoResultsVisible: false,
			componentProps: {
				simple: {
					component: Simple,
					data: {},
				},
				modal: {
					component: FlytModal,
					data: {
						selector: "#m-input",
						type: "drawerUp",
						visible: false,
					},
				},
			},
		}
	},

	props: {
		locationType: {
			type: String
		},
		placeholder: {
			type: String
		},
		focusOnLoad: {
			type: Boolean,
			default: false
		},
		hasError: {
			type: Boolean,
			default: false
		}
	},

	name: "FlytLocationSelector",

	components: {
		vSelect,
		PopularSearches,
		SelectOption,
		Switcher,
		FlytModal,
		Simple,
		AirportIconWithBorder,
		HelipadIconWithBorder,
		PinIconWithBorder
	},

	computed: {
		...mapState({
			value(state) {
				return state.bookingForm.locations[this.locationType].value
			},
		}),
		...mapGetters("bookingForm", ["aircraftCategory", "isLocationEmpty"]),
		...mapGetters("map", ["cssSelector", "hoveredLocationID"]),
		...mapGetters("browser", ["isMobile"]),

		/**
		 * Is this wrapped in just a transparent layer, for desktop,
		 * or a portal component to allow all mobile interaction.
		 */
		currentWrapper() {
			let component = this.isMobile ? "modal" : "simple"
			return this.componentProps[component]
		},

		/**
		 * Convenience wrapper for this.componentProps.modal.data.visible
		 */
		isModalVisible: {
			get: function () {
				return this.componentProps.modal.data.visible
			},
			set: function (isVisible) {
				this.componentProps.modal.data.visible = isVisible
			},
		}
	},

	methods: {
		...mapGetters("bookingForm", ["defaultDestinationLocationSet"]),
		...mapActions("bookingForm", [
			"storeLocationValue",
			"deleteLocationValue"
		]),
		...mapMutations("map", [
			"setHoveredLocationID",
			"clearHoveredLocationID",
			"setSearchingMarkers",
			"clearSearchingMarkers",
			"setJourneyMarker",
			"clearJourneyMarker"
		]),

		/**
		 * When input has focus, listen for subsequent clicks
		 */
		focusListener() {
			this.$nextTick(function () {
				document.body.addEventListener("mousedown", this.clickListener)
			})
		},

		/**
		 * If map or this input isn't clicked, clear the input entirely, and remove click listener
		 */
		clickListener(event) {
			const mapClicked = event.target.closest(this.cssSelector) !== null
			const thisClicked = event.target.closest(`[data-ref="${this.locationType}"]`) !== null
			if (mapClicked || thisClicked) return
			this.clearAllOptions()
			this.clearInputText()
			document.body.removeEventListener("click", this.clickListener)
		},

		/**
		 * Force the search component's search data to empty string
		 */
		clearInputText() {
			let select = this.$refs[this.locationType]
			if (select)
				select.search = ''
		},

		/**
		 * Reset all existing search data
		 */
		clearSearchResults() {
			this.clearAllOptions()
			this.isNoResultsVisible = false
			this.currentPrediction = ''
		},

		/**
		 * Cancels the previous search
		 */
		cancelSearch() {
			if (cancel !== undefined) cancel()
		},

		/**
		 * If not enough characters entered, cancel the previous
		 * search, and clear all options and map marker data.
		 */
		onSearchTermTooShort() {
			this.cancelSearch()
			this.clearSearchResults()
			this.clearSearchingMarkers()
		},

		/**
		 * If input has 3+ characters, begin fetching options
		 */
		onInputChange(searchTerm, isLoading) {
			if (searchTerm.length < 3) {
				this.onSearchTermTooShort()
			} else {
				this.cancelSearch()
				this.fetchLocations(searchTerm, isLoading)
			}
		},

		/**
		 * Debounced every 300ms, fetch locations from Flyt API
		 */
		fetchLocations: debounce(function (searchTerm, isLoading) {
			let resource = `bookings/get-closest-locations/${this.aircraftCategory}/${searchTerm}`
			let config = {
				cancelToken: new ApiService.CancelToken(function executor(c) { cancel = c })
			}
			isLoading(true)
			ApiService.get(resource, config)
				.then(response => this.onSearchCompleted(response.data))
				.catch(() => this.onSearchUnsuccessful())
				.then(() => isLoading(false))
		}, 300),

		/**
		 * Store the number of locations found and check the prediction.
		 * If there are locations, run the update
		 */
		onSearchCompleted({addressPrediction, helipads, privateSite}) {
			this.numberOfLocationsFound = helipads.length
			let newPrediction = addressPrediction.formatted_address

			if (!this.predictionHasChanged(newPrediction))
				return

			this.updatePrediction(newPrediction)

			if (this.numberOfLocationsFound === 0) {
				this.onSearchUnsuccessful()
				return
			}

			this.onSearchSuccessful(addressPrediction, helipads, privateSite)
		},

		/**
		 * Create options array and set the map markers
		 */
		onSearchSuccessful(geoArea, locations, privateSite) {
			this.setOptions(geoArea, locations, privateSite)
			this.setSearchingMarkers(this.optionsList)
			this.isNoResultsVisible = false
		},

		/**
		 * Reset all options and clear any markers
		 */
		onSearchUnsuccessful() {
			this.clearAllOptions()
			this.clearSearchingMarkers()
			this.updatePrediction(null)
			this.isNoResultsVisible = true
		},

		/**
		 * Format the geoArea and locations results
		 * and create the option array from them.
		 */
		setOptions(geoArea, locations, privateSite) {
			// Geolocations taking too long to process from API
			// keep this code comment here to quickly revert
			// ------------------------------------------------
			// this.searchGeoArea = this.formatArea(geoArea)
			// this.searchLocations = locations.map(location => this.formatLocation(location))
			// this.optionsList = [this.searchGeoArea, ...this.searchLocations]
			this.searchLocations = [...locations.slice(0, 5).map(location => this.formatLocation(location)), {...privateSite, id: 0, isPrivate: true, is_private: true, locationType: this.locationType}];
			this.optionsList = [...this.searchLocations]
		},

		/**
		 * Reset all location data to initial state
		 */
		clearAllOptions() {
			this.searchGeoArea = null
			this.searchLocations = null
			this.optionsList = []
		},

		/**
		 * Compare new search predicition with the previously stored one.
		 */
		predictionHasChanged(newPrediction) {
			return newPrediction !== this.currentPrediction
		},

		/**
		 * Save new address prediction
		 */
		updatePrediction(prediction) {
			this.currentPrediction = prediction ? prediction : ""
		},

		/**
		 * When selected value updates, store the location, or reset if empty.
		 */
		onSelect(newValue) {
			console.log('newValue', newValue)
			if (newValue === null) this.clearSelection()
			else this.confirmSelection(newValue)
		},

		/**
		 * Format selected popular location, and save
		 */
		onSelectPopular(newValue) {
			let formattedLocation = this.formatLocation(newValue)
			this.confirmSelection(formattedLocation)
		},

		/**
		 * When new value selected, store the journey marker,
		 * clear all searching markers, store the journey
		 * location, and reset everything UI-related.
		 */
		confirmSelection(value) {
			console.log('value', value);
			this.setJourneyMarker({
				newMarker: value,
				locationType: this.locationType
			})
			this.storeLocationValue({
				value,
				locationType: this.locationType
			})
			this.clearSearchingMarkers()
			this.closeMobileSearch()
			this.clearHoveredLocationID()
		},

		/**
		 * On delete, remove the stored location, associated transfer, and any options
		 */
		clearSelection() {
			this.deleteLocationValue(this.locationType)
			this.clearJourneyMarker(this.locationType)
			this.clearAllOptions()
		},

		/**
		 * Open mobile drawer-style input
		 */
		openMobileSearch(evt) {
			evt.srcElement.blur()
			this.isModalVisible = true
		},

		/**
		 * Focus the input after the mobile selector opens.
		 */
		focusMobileSearch() {
			let input = document.getElementById(this.inputID)
			this.$nextTick(() => {
				setTimeout(() => input.focus())
			})
		},

		/**
		 * Close mobile drawer-style input
		 */
		closeMobileSearch() {
			this.isModalVisible = false
		},

		/**
		 * Get required fields from search term
		 */
		formatArea(area) {
			return {
				id: 0,
				isGeoArea: true,
				locationType: this.locationType,
				primary: area.formatted_address,
				secondary: null,
				lat: area.geometry.location.lat,
				lng: area.geometry.location.lng,
				distance: null
			}
		},

		/**
		 * Get required fields from each result
		 */
		formatLocation(location) {
			return {
				id: location.id,
				isGeoArea: false,
				locationType: this.locationType,
				primary: location.name,
				secondary: location.address,
				lat: location.lat,
				lng: location.lng,
				distance: location.distance_in_miles,
				isPrivate: location.is_private,
				is_private: location.is_private
			}
		},

		/**
		 * Get the icon, depending on the type of location
		 */
		getOptionIcon(option) {
			if (option.isGeoArea)
				return PinIconWithBorder

			let icon
			switch (this.aircraftCategory) {
				case 'aeroplane':
					icon = AirportIconWithBorder
					break;
				case 'helicopter':
					icon = HelipadIconWithBorder
					break;
			}
			return icon
		},

		/**
		 * If an object with the right id is found and the
		 * location type is the same as the component's,
		 * confirm the selection and clear the input.
		 */
		selectLocationByID(id, type) {
			if (type !== this.locationType)
				return

			let location = this.optionsList.find(option => option.id === id)
			this.confirmSelection(location)
			this.clearInputText()
		}
	},

	mounted() {
		this.$root.$on('selectLocationByID', (id, type) => this.selectLocationByID(id, type))
	}
}
</script>

<style lang="scss" scoped>
.private-head, .recommended-head {
	display: flex;
	flex-direction: row;
	font-size: .75rem;
	padding: 0.75rem;
	text-transform: uppercase;
	line-height: .5rem;
	color: $colour-pencil;
}
// Main search component
.location-selector {
	&.is-mobile {
		bottom: 0;
		left: 0;
		overflow: hidden;
		top: 0;
		width: 100%;
	}
}

.location-selector__placeholder {
	color: $colour-pencil;
	display: inline-flex;
	align-items: center;
}

// Mobile cancel search button
.location-selector__m-cancel {
	background-color: transparent;
	background-image: asset("icons_close.svg");
	background-position: 50% 50%;
	background-repeat: no-repeat;
	background-size: 1.5rem;
	position: absolute;
	right: 0.75rem;
	top: 0.75rem;
	width: 3rem;
}

// No results 
.location-selector__no-options {
	background: #fff;
	padding: 0.5rem 0.75rem;
}

.vs__clear, /* Mobile */
.location-selector ::v-deep .vs__clear /* Desktop */ {
	align-items: center;
	background: $colour-teal;
	border: none;
	border-radius: 50%;
	display: flex;
	height: 1.5rem;
	justify-content: center;
	min-height: 1.5rem;
	min-width: 1.5rem;
	padding: 0;
	pointer-events: auto;
	position: absolute;
	right: 0.75rem;
	top: 50%;
	transform: translateY(-50%);
	width: 1.5rem;
	// Hide default plugin cross logo
	svg {
		display: none;
	}
	// Custom cross logo
	&:before,
	&:after {
		background: #fff;
		content: "";
		display: block;
		height: 0.125rem;
		left: 50%;
		position: absolute;
		top: 50%;
		width: 0.75rem;
	}
	&:before {
		transform: translate(-50%, -50%) rotate(-45deg);
	}
	&:after {
		transform: translate(-50%, -50%) rotate(45deg);
	}
}

::v-deep .vs__selected-options {
	.is-mobile & {
		background: #fff;
		margin-right: 3rem;
	}
}

// Options list
.location-selector__options-header {
	background: $colour-teal;
	color: #fff;
	padding: 0.75rem 0.75rem 0.5rem;
}

// Selected option
.location-selector__selected,
.location-selector__m-selected {
	border-radius: 0.25rem;
	box-shadow: 0 0.0625rem 0.25rem rgba(0, 0, 0, 0.15), 0 0.03125rem 0.125rem rgba(0, 0, 0, 0.05);
	display: block;
	line-height: 1.25;
	padding: 0.5rem 3rem 0.5rem 0.75rem;
	width: 100%;
}

.location-selector__selected ::v-deep + input {
	display: none;
}

// Hide input only when location-selector__selected appears directly before it
.is-mobile .location-selector__selected {
	display: none;
}

.is-mobile .location-selector__selected + ::v-deep input {
	display: block;
}

.location-selector__selected-name {
	margin: 0.125rem 0;
}

.location-selector__selected-address {
	color: $colour-pencil--dark;
}

.location-selector__approval {
	line-heigth: 1.25rem;
	color: #ee9346;
}

// Options list item
::v-deep .vs__dropdown-menu {
	background: #fff;
	border-radius: 0.25rem;
	box-shadow: 0 0 0.5rem rgba(0, 0, 0, 0.15);
	left: 2rem;
	overflow: hidden auto;
	max-height: 35rem;
	position: absolute;
	right: 0;
	top: 100%;
	z-index: 10;
	// Make heading sticky
	li:nth-of-type(1):not(.vs__dropdown-option) {
		position: sticky;
		top: 0;
		z-index: 10;
	}
}
// Mobile options list item
.is-mobile ::v-deep .vs__dropdown-menu {
	background: none;
	border-radius: 0;
	box-shadow: none;
	left: 0;
	max-height: calc(100% - 5rem);
	overflow-y: scroll;
	overflow-y: auto;
	overflow-y: overlay;
	top: 5rem;
}

/* Propeller loading indicator */
::v-deep .location-selector__loading {
	animation: spinner 1s linear infinite;
	background-image: asset("./icons_propeller--teal.svg");
	background-repeat: no-repeat;
	background-size: contain;
	display: block;
	height: 2rem;
	position: absolute;
	right: 0.5rem;
	top: 0.5rem;
	width: 2rem;
	z-index: 1;
	.is-mobile & {
		right: 4.5rem;
		top: 1rem;
	}
}

.v-select.is-selected {
	pointer-events: none;
}

.hidden {
	display: none;
}
</style>